import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { changePassword } from "../auth/session";
import { PasswordRequirements, passwordMeetsPolicy } from "./PasswordRequirements";

export function ChangePasswordDialog(props: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!props.open) {
      setCurrent("");
      setNext("");
      setConfirm("");
      setError(null);
      setSuccess(false);
    }
  }, [props.open]);

  const mutation = useMutation({
    mutationFn: () => changePassword({ oldPassword: current, newPassword: next }),
    onSuccess: () => {
      setSuccess(true);
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : t("common.error"));
    }
  });

  const policyOk = passwordMeetsPolicy(next);
  const matchOk = next.length > 0 && next === confirm;
  const canSubmit = !!current && policyOk && matchOk && !mutation.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    mutation.mutate();
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("auth.changePassword.title")}</DialogTitle>
          <DialogDescription>{t("auth.changePassword.description")}</DialogDescription>
        </DialogHeader>
        {success ? (
          <div className="space-y-3">
            <p className="text-sm text-green-700 dark:text-green-300">{t("auth.changePassword.successMessage")}</p>
            <DialogFooter>
              <Button onClick={() => props.onOpenChange(false)}>{t("common.close")}</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">{t("auth.changePassword.currentLabel")}</label>
              <Input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                disabled={mutation.isPending}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">{t("auth.changePassword.newLabel")}</label>
              <Input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                disabled={mutation.isPending}
              />
              <PasswordRequirements password={next} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">{t("auth.changePassword.confirmLabel")}</label>
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                disabled={mutation.isPending}
              />
              {confirm.length > 0 && !matchOk && (
                <p className="text-xs text-destructive">{t("auth.changePassword.mismatch")}</p>
              )}
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => props.onOpenChange(false)} disabled={mutation.isPending}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {mutation.isPending ? t("common.saving") : t("auth.changePassword.submit")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
